import React, { useMemo, useState } from 'react';
import { CheckIcon, FolderIcon, InfoIcon, Loader2Icon } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { PillButton } from '../ui/PillButton';
import { ColorCheckbox } from '../ui/ColorCheckbox';
import { InfoTip } from '../ui/InfoTip';
import { Segmented } from '../ui/Segmented';
import { getViewportBridge } from '../../utils/viewportBridge';
import { exportMesh, fileExtension, modelHasTextures, pickSaveLocation, saveBlob, type ExportUnits, type MeshFormat, type StlEncoding } from '../../utils/meshExport';
type ExportMeshModalProps = {
  partName: string;
  onClose: () => void;
};
type Status = 'idle' | 'exporting' | 'done' | 'error';
const formatHints: Record<MeshFormat, string> = {
  stl: 'Works with every slicer and CAD tool. Geometry only.',
  obj: 'Widely supported for rendering and editing. Keeps UVs and textures.',
  '3mf': 'Modern print format. Stores units and color textures in a single file.'
};
const sanitize = (s: string) => s.replace(/[\\/:*?"<>|]+/g, '').trim();
function CheckRow({
  label,
  info,
  checked,
  disabled,
  onChange
}: {
  label: string;
  info: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return <div className={`flex h-[26px] items-center gap-[10px] ${disabled ? 'pointer-events-none opacity-50' : ''}`} data-id="element-141" data-mp="26o9tovvrl3no">
      <ColorCheckbox label={label} checked={checked} onChange={onChange} data-id="element-142" data-mp="21pyhiehzyh2a" />
      <button type="button" tabIndex={-1} onClick={() => onChange(!checked)} className="text-[12.5px] text-pf-ink" data-id="element-143" data-mp="1wsg7eks8tvsp">
        {label}
      </button>
      <InfoTip label={info} data-id="element-144" data-mp="1ss64xi3ntnil" />
    </div>;
}
function SectionLabel({
  children
}: {
  children: React.ReactNode;
}) {
  return <h3 className="pb-[6px] pt-[12px] text-[12.5px] font-bold text-pf-ink first:pt-0" data-id="element-145" data-mp="1irvozpq8q1zq">{children}</h3>;
}
export function ExportMeshModal({
  partName,
  onClose
}: ExportMeshModalProps) {
  const model = getViewportBridge()?.getModel() ?? null;
  const hasTextures = useMemo(() => !!model && modelHasTextures(model), [model]);
  const [format, setFormat] = useState<MeshFormat>('3mf');
  const [orientation, setOrientation] = useState(true);
  const [scale, setScale] = useState(true);
  const [textures, setTextures] = useState(hasTextures);
  const [units, setUnits] = useState<ExportUnits>('mm');
  const [stlEncoding, setStlEncoding] = useState<StlEncoding>('binary');
  const [normals, setNormals] = useState(true);
  const [baseName, setBaseName] = useState(sanitize(partName) || 'model');
  const [handle, setHandle] = useState<FileSystemFileHandle | null>(null);
  const [pickerUnavailable, setPickerUnavailable] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const texturesAllowed = format !== 'stl' && hasTextures;
  const includeTextures = texturesAllowed && textures;
  const ext = fileExtension({
    format,
    textures: includeTextures
  });
  const fileName = `${sanitize(baseName) || 'model'}.${ext}`;
  const changeFormat = (f: MeshFormat) => {
    setFormat(f);
    setHandle(null);
  };
  const choose = async () => {
    try {
      const h = await pickSaveLocation(fileName);
      if (!h) return;
      setHandle(h);
      setBaseName(h.name.replace(/\.[^.]+$/, ''));
    } catch {
      setPickerUnavailable(true);
    }
  };
  const runExport = async () => {
    const bridge = getViewportBridge();
    const m = bridge?.getModel();
    if (!bridge || !m) {
      setStatus('error');
      return;
    }
    setStatus('exporting');
    try {
      await new Promise(r => requestAnimationFrame(r));
      const blob = await exportMesh(m, bridge.getOrientation(), {
        format,
        orientation,
        scale,
        textures: includeTextures,
        units,
        stlEncoding,
        normals
      }, sanitize(baseName) || 'model');
      await saveBlob(blob, fileName, handle);
      setStatus('done');
      window.setTimeout(onClose, 900);
    } catch {
      setStatus('error');
    }
  };
  const busy = status === 'exporting' || status === 'done';
  return <Modal title="Export Mesh" description="Export the selected model without supports or print scaffolding." onRequestClose={() => !busy && onClose()} width={440} footer={<>
          {status === 'error' && <p className="mr-auto text-[11.5px] text-[#c62828]" role="alert" data-id="element-147" data-mp="2882z5uqq8rxb">Export failed. Try again or pick another format.</p>}
          <PillButton className={`h-[28px] w-[110px] flex-none ${status === 'error' ? '' : 'ml-auto'}`} onClick={onClose} disabled={busy} data-id="element-148" data-mp="2czl0wl0kqwjc">
            Cancel
          </PillButton>
          <PillButton variant="primary" className="h-[28px] w-[110px] flex-none" onClick={runExport} disabled={busy || !model} data-id="element-149" data-mp="2lyojzozqrbxu">
            {status === 'exporting' && <Loader2Icon className="h-3 w-3 animate-spin" data-id="element-150" data-mp="2qw7y8owfvops" />}
            {status === 'done' && <CheckIcon className="h-3 w-3" strokeWidth={3} data-id="element-151" data-mp="2vwmakj52pktm" />}
            {status === 'exporting' ? 'Exporting…' : status === 'done' ? 'Exported' : 'Export'}
          </PillButton>
        </>} data-id="element-146" data-mp="1lk5wa5o3cbk2">
      
      <SectionLabel data-id="element-152" data-mp="11jmnk4yd5ocb">Format</SectionLabel>
      <Segmented label="Export format" value={format} onChange={changeFormat} options={[{
      value: 'stl',
      label: 'STL'
    }, {
      value: 'obj',
      label: 'OBJ'
    }, {
      value: '3mf',
      label: '3MF'
    }]} data-id="element-153" data-mp="tzlkx7jqg405" />
      
      <p className="pt-[6px] text-[11.5px] text-pf-muted" data-id="element-154" data-mp="kabt7ssswnck">{formatHints[format]}</p>

      <SectionLabel data-id="element-155" data-mp="fcil2u7xtj9n">Exported geometry</SectionLabel>
      <CheckRow label="Current orientation" info="Keep the rotation applied in PreForm. Turn off to export the model as originally imported." checked={orientation} onChange={setOrientation} data-id="element-156" data-mp="1cyfwy8rtaqcm" />
      <CheckRow label="Current scale" info="Keep the size shown on the build platform. Turn off to use the original file's dimensions." checked={scale} onChange={setScale} data-id="element-157" data-mp="130jr9whn8nlw" />
      <CheckRow label="Include textures" info={format === 'obj' ? 'Adds a material file and texture images, packaged together as a .zip.' : 'Embeds the color texture inside the 3MF file.'} checked={includeTextures} disabled={!texturesAllowed} onChange={setTextures} data-id="element-158" data-mp="y3jgj89umwog" />
      
      <p className="pt-[2px] text-[11px] text-pf-muted" data-id="element-159" data-mp="2etu3k0o3lhz9">
        {!hasTextures ? 'This model has no textures to include.' : 'Textures are only available for OBJ and 3MF formats.'}
      </p>

      <SectionLabel data-id="element-160" data-mp="20wf92hizmk0f">{format.toUpperCase()} options</SectionLabel>
      <div className="flex flex-col gap-[8px]" data-id="element-161" data-mp="25ruohdwn8nyy">
        <div className="flex items-center justify-between gap-3" data-id="element-162" data-mp="1r49gb5kwvyk6">
          <span className="flex items-center gap-[5px] text-[12px] text-pf-muted" data-id="element-163" data-mp="1r2ygthyi1cyv">
            Units <InfoTip label="The unit coordinates are written in. Most slicers expect millimeters." data-id="element-164" data-mp="1vwj3weyk0mja" />
          </span>
          <div className="w-[180px]" data-id="element-165" data-mp="1i1gsjcaxu4z3">
            <Segmented label="Units" size="sm" value={units} onChange={setUnits} options={[{
            value: 'mm',
            label: 'Millimeters'
          }, {
            value: 'in',
            label: 'Inches'
          }]} data-id="element-166" data-mp="1n24tapoyy06i" />
          </div>
        </div>
        {format === 'stl' && <div className="flex items-center justify-between gap-3" data-id="element-167" data-mp="1d35gqw131ke8">
            <span className="flex items-center gap-[5px] text-[12px] text-pf-muted" data-id="element-168" data-mp="ti59cp03j6s9">
              Encoding <InfoTip label="Binary files are about 5× smaller. ASCII is human-readable text." data-id="element-169" data-mp="phd03vr851vz" />
            </span>
            <div className="w-[180px]" data-id="element-170" data-mp="13h4kce8c3eif">
              <Segmented label="STL encoding" size="sm" value={stlEncoding} onChange={setStlEncoding} options={[{
            value: 'binary',
            label: 'Binary'
          }, {
            value: 'ascii',
            label: 'ASCII'
          }]} data-id="element-171" data-mp="13i5yirknch4d" />
            </div>
          </div>}
        {format === 'obj' && <CheckRow label="Include normals" info="Keeps smooth shading in other apps. Turn off for a smaller file." checked={normals} onChange={setNormals} data-id="element-172" data-mp="18gqwekcfeopn" />}
      </div>

      <div className="mt-[12px] flex items-center gap-[8px] rounded-[6px] bg-pf-blue-soft px-[10px] py-[8px] text-[12px] text-pf-ink" data-id="element-173" data-mp="1h8j4iodz9lr2">
        <InfoIcon className="h-[14px] w-[14px] shrink-0 text-pf-blue" strokeWidth={2.2} data-id="element-174" data-mp="1w6bi3azys02m" />
        Supports, rafts, and touchpoints are not included.
      </div>

      <SectionLabel data-id="element-175" data-mp="bud01js9e53r">File name</SectionLabel>
      <div className="flex h-[28px] items-center rounded-[4px] border border-pf-line bg-white pl-2 pr-2 text-[12px] focus-within:border-pf-blue-line" data-id="element-176" data-mp="btbcwgxyxs5o">
        <input aria-label="File name" value={baseName} onChange={e => {
        setBaseName(e.target.value);
        setHandle(null);
      }} className="min-w-0 flex-1 bg-transparent text-pf-ink outline-none" data-id="element-177" data-mp="lsk2yyppqmmr" />
        
        <span className="shrink-0 text-pf-muted" data-id="element-178" data-mp="3p6wmht4okvsi">.{ext}</span>
      </div>

      <SectionLabel data-id="element-179" data-mp="3u8lx57nzr7g2">Location</SectionLabel>
      <div className="flex h-[34px] items-center gap-[8px] rounded-[4px] border border-pf-line bg-white pl-[10px] pr-[4px]" data-id="element-180" data-mp="31szuewbjsgt">
        <FolderIcon className="h-[14px] w-[14px] shrink-0 fill-pf-blue text-pf-blue" strokeWidth={1.8} data-id="element-181" data-mp="7srfdc9hgmj0" />
        <span className="min-w-0 flex-1 truncate text-[12px] text-pf-ink" data-id="element-182" data-mp="36jb1u5peu59b">{handle ? `Chosen location · ${handle.name}` : 'Downloads'}</span>
        <PillButton className="h-[24px] w-[84px] flex-none rounded-[4px]" onClick={choose} disabled={pickerUnavailable || busy} data-id="element-183" data-mp="3bfrpkysp6hlv">
          Choose…
        </PillButton>
      </div>
      {pickerUnavailable && <p className="pt-[4px] text-[11px] text-pf-muted" data-id="element-184" data-mp="1oisagbj4mqlh">This browser saves exports to your Downloads folder.</p>}
    </Modal>;
}